import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import UserEmailBadge from '../components/UserEmailBadge'
import DaySelector from '../components/DaySelector'
import DailyMetricsForm from '../components/DailyMetricsForm'
import { formatDateToYMD, startOfDay } from '../utils/metricDateUtils'

const CustomerDailyDataEntryPage = () => {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const { t, i18n } = useTranslation(['dashboard', 'common'])

  const defaultDay = useMemo(() => startOfDay(new Date()), [])

  const [selectedDay, setSelectedDay] = useState(defaultDay)
  const [currentData, setCurrentData] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    fetchDataForDay()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDay, user])

  const handleLogout = async () => {
    const { error } = await signOut()
    if (error) console.error('Error signing out:', error)
    navigate('/login')
  }

  const fetchDataForDay = async () => {
    if (!user || !selectedDay) return

    try {
      setLoading(true)

      const { data: business, error: businessError } = await supabase
        .from('businesses')
        .select('id')
        .eq('owner_email', user.email)
        .single()

      if (businessError) {
        console.log('No business found for user')
        return
      }

      const dayKey = formatDateToYMD(startOfDay(selectedDay))

      const { data: metrics, error: metricsError } = await supabase
        .from('daily_metrics')
        .select('*')
        .eq('business_id', business.id)
        .eq('metric_date', dayKey)
        .single()

      if (metricsError) {
        setCurrentData(null)
      } else {
        setCurrentData(metrics)
      }
    } catch (error) {
      console.error('Error fetching daily data:', error)
      setCurrentData(null)
    } finally {
      setLoading(false)
    }
  }

  const handleDayChange = (newDay) => {
    setSelectedDay(newDay)
  }

  const handleDataSave = (savedData) => {
    setCurrentData(savedData)
  }

  const handleFirstMetricSaved = () => {
    navigate('/dashboard')
  }

  const formatDayDisplay = (day) => {
    return startOfDay(day).toLocaleDateString(i18n.language === 'es' ? 'es-ES' : 'en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-5 sm:py-6">
            <h1 className="text-3xl font-bold text-gray-900">
              <span>Interactive </span>
              <span className="text-[#FF1B54]">Business</span>
            </h1>

            <div className="flex items-center space-x-4">
              <UserEmailBadge email={user?.email} showProfileMenu />
              <button
                onClick={handleLogout}
                className="bg-[#FF1B54] hover:bg-[#E1174C] text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-[#FF1B54] focus:ring-offset-2"
              >
                {t('navigation.logout', { ns: 'common' })}
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-5 sm:px-0 sm:py-7">
          <div className="mb-7">
            <div className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">
              {t('customer.subtitle')}
            </div>
            <p className="mt-2 text-base leading-6 text-gray-600">
              {t('customer.dailyDataEntrySubtitle')}
            </p>
          </div>
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex items-end gap-5 sm:gap-7">
              <button
                onClick={() => navigate('/dashboard')}
                className="bg-[#FF1B54] hover:bg-[#E1174C] text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-[#FF1B54] focus:ring-offset-2"
              >
                {t('navigation.dashboard', { ns: 'common' })}
              </button>
              <span className="py-2.5 px-1 border-b-2 border-[#FF1B54] font-semibold text-sm text-[#FF1B54]">
                {t('customer.dailyMetrics')}
              </span>
            </nav>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-[minmax(340px,0.9fr)_minmax(0,2.1fr)] gap-5 xl:gap-6 items-start">
              <div>
                <DaySelector selectedDay={selectedDay} onDayChange={handleDayChange} />

                {currentData && (
                  <div className="mt-5 bg-white border border-gray-200 shadow-sm rounded-xl p-5">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">
                      {t('customer.currentDataFor', { period: formatDayDisplay(selectedDay) })}
                    </h4>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('dataEntry.fields.revenue')}:</span>
                        <span className="font-semibold text-gray-900">
                          ${currentData.revenue?.toLocaleString() || '0'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('dataEntry.fields.expenses')}:</span>
                        <span className="font-semibold text-gray-900">
                          ${currentData.expenses?.toLocaleString() || '0'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('dataEntry.fields.leads')}:</span>
                        <span className="font-semibold text-gray-900">
                          {currentData.leads?.toLocaleString() || '0'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('dataEntry.fields.customers')}:</span>
                        <span className="font-semibold text-gray-900">
                          {currentData.customers?.toLocaleString() || '0'}
                        </span>
                      </div>
                      {/* Social Following (temporarily removed) */}
                      {/**
                       * TODO: Re-enable when we decide how/where it should be used.
                       */}
                      {/*
                      <div className="flex justify-between">
                        <span className="text-gray-600">Social Following:</span>
                        <span className="font-medium">
                          {currentData.social_following?.toLocaleString() || '0'}
                        </span>
                      </div>
                      */}
                    </div>
                  </div>
                )}
              </div>

              <div className="min-w-0">
                {loading ? (
                  <div className="bg-white border border-gray-200 border-t-[3px] border-t-[#FF1B54] shadow-sm rounded-2xl p-6 sm:p-8">
                    <div className="flex justify-center items-center h-64">
                      <div className="animate-spin rounded-full h-10 w-10 border-[3px] border-gray-200 border-b-[#FF1B54]"></div>
                    </div>
                  </div>
                ) : (
                  <DailyMetricsForm
                    selectedDay={selectedDay}
                    existingData={currentData}
                    onSave={handleDataSave}
                    onDataChange={fetchDataForDay}
                    onFirstMetricSaved={handleFirstMetricSaved}
                  />
                )}
              </div>
          </div>
        </div>
      </main>
    </div>
  )
}

export default CustomerDailyDataEntryPage
